import { useEffect, useRef, useState } from "react";

import { toastError } from "@alpacahq/ui/old";

import { getErrorMessage, useGetAgreementPreview } from "~/v2/api/hooks";
import { PDFAgreementModal } from "~/v2/components/form";

export interface AgreementPreviewModalProps {
  isOpen: boolean;
  title: string;
  accountId: string;
  agreementId: string;
  hasAgreed: boolean;
  isSubmitting?: boolean;
  onCancel: () => void;
  onAgree: () => void;
}

export const AgreementPreviewModal = ({
  isOpen,
  title,
  accountId,
  agreementId,
  hasAgreed,
  isSubmitting,
  onCancel,
  onAgree,
}: AgreementPreviewModalProps) => {
  const [previewUrl, setPreviewUrl] = useState("");

  const {
    data: previewBlob,
    error: previewError,
    errorUpdatedAt,
  } = useGetAgreementPreview({
    accountId,
    agreement: agreementId,
    options: {
      enabled: isOpen && !!accountId && !!agreementId,
      // Handled below with a cancel side-effect; suppress the shared toast.
      meta: { showErrorToast: false },
    },
  });

  // `onCancel` is a new reference every parent render, so guard on
  // `errorUpdatedAt` to only run this once per new error.
  const handledErrorAt = useRef<number>();

  useEffect(() => {
    if (
      !previewError ||
      !errorUpdatedAt ||
      handledErrorAt.current === errorUpdatedAt
    ) {
      return;
    }
    handledErrorAt.current = errorUpdatedAt;
    toastError(
      `Failed to load agreement preview: ${getErrorMessage(previewError)}`,
    );
    onCancel();
  }, [previewError, errorUpdatedAt, onCancel]);

  // Render the downloaded PDF from a blob URL (forcing the PDF MIME type so the
  // iframe renders inline) and revoke it whenever the preview changes/unmounts.
  useEffect(() => {
    if (!previewBlob) {
      setPreviewUrl("");
      return;
    }

    const url = window.URL.createObjectURL(
      new Blob([previewBlob], { type: "application/pdf" }),
    );
    setPreviewUrl(url);

    return () => {
      window.URL.revokeObjectURL(url);
    };
  }, [previewBlob]);

  return (
    <PDFAgreementModal
      isOpen={isOpen}
      title={title}
      url={previewUrl}
      isPreviewLoading={isOpen && !previewUrl}
      hasAgreed={hasAgreed}
      isSubmitting={isSubmitting}
      onCancel={onCancel}
      onAgree={onAgree}
    />
  );
};
